import {
  Box,
  VStack,
  Heading,
  Text,
  Badge,
  HStack,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { AlertTriangle, Calendar, User as UserIcon, Building2, AlertCircle } from 'lucide-react';
import type { CompanyIssuesListProps } from '../dashboard.types';
import { formatDateWithoutTimezone } from '../dashboard.util';

/**
 * Company Issues List Component
 * Displays company issues as clickable cards with priority badges and metadata
 */
export function CompanyIssuesList({
  issues,
  users,
  departments,
  onIssueClick,
  onViewAllClick,
}: CompanyIssuesListProps) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.800');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const emptyIconColor = useColorModeValue('gray.300', 'gray.600');

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getUserName = (userId?: string) => {
    if (!userId) return null;
    const user = users.find((u) => u.id === userId);
    return user?.name || 'Unknown';
  };

  const getDepartmentName = (departmentId?: string) => {
    if (!departmentId) return null;
    const dept = departments.find((d) => d.id === departmentId);
    return dept?.name || 'Unknown';
  };

  if (issues.length === 0) {
    return (
      <Box bg={cardBg} rounded="lg" borderWidth="1px" borderColor={borderColor} p={12} textAlign="center">
        <Icon as={AlertTriangle} w={16} h={16} color={emptyIconColor} mx="auto" mb={4} />
        <Heading size="md" color={headingColor} mb={2}>
          No Company Issues
        </Heading>
        <Text color={textColor}>
          No company issues found. Create issues on the Issues page.
        </Text>
      </Box>
    );
  }

  return (
    <Box bg={cardBg} rounded="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden">
      <VStack spacing={0} align="stretch" divider={<Box borderTopWidth="1px" borderColor={borderColor} />}>
        {issues.map((issue) => (
          <Box
            key={issue.id}
            p={6}
            cursor="pointer"
            onClick={() => onIssueClick(issue.id)}
            _hover={{ bg: hoverBg }}
            transition="background-color 0.2s"
          >
            <HStack justify="space-between" align="flex-start">
              <Box flex="1">
                {/* Issue Name and Priority */}
                <HStack spacing={3} mb={2}>
                  <Heading size="md" color={headingColor}>
                    {issue.name}
                  </Heading>
                  <Badge
                    colorScheme={getPriorityColor(issue.priority)}
                    px={2}
                    py={1}
                    rounded="full"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {issue.priority}
                  </Badge>
                </HStack>

                {/* Description */}
                {issue.description && (
                  <Text fontSize="sm" color={textColor} mb={3}>
                    {issue.description}
                  </Text>
                )}

                {/* Metadata */}
                <HStack spacing={4} fontSize="sm" color={textColor}>
                  <HStack spacing={1}>
                    <Icon as={Calendar} w={4} h={4} />
                    <Text>
                      {issue.createdAt && formatDateWithoutTimezone(issue.createdAt)}
                    </Text>
                  </HStack>
                  {issue.assignedToUserId && (
                    <HStack spacing={1}>
                      <Icon as={UserIcon} w={4} h={4} />
                      <Text>{getUserName(issue.assignedToUserId)}</Text>
                    </HStack>
                  )}
                  {issue.departmentId && (
                    <HStack spacing={1}>
                      <Icon as={Building2} w={4} h={4} />
                      <Text>{getDepartmentName(issue.departmentId)}</Text>
                    </HStack>
                  )}
                </HStack>
              </Box>
              <Icon as={AlertCircle} w={5} h={5} color="yellow.600" flexShrink={0} ml={4} />
            </HStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

